import { useState } from 'react';
import { FileInputOptions } from 'ra-ui-materialui/lib/input/FileInput';
import { Grid, FormControl, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import {
  FileInput,
  FileInputProps,
  ImageField,
  FormDataConsumer,
  InputProps,
  UrlField,
  useInput
} from 'react-admin';
import get from 'lodash/get';
import { Upload } from 'tus-js-client';

import config from '../config';

const useStyles = makeStyles(theme => ({
  root: { boxSizing: 'border-box', padding: '0 12px 12px' },
  upload: {
    '& .previews': { display: 'none' }
  },
  progress: { width: '100%' },
  helperText: { whiteSpace: 'pre-line' }
}));

type UploadInputProps = FileInputProps & InputProps<FileInputOptions>;

export default function UploadInput(props: UploadInputProps) {
  const {
    label,
    mimeTypes,
    // record,
    // validate,
    source,
    className,
    helperText
  } = props;
  const classes = useStyles(props);

  const {
    input: { onChange },
    // meta,
    meta: { error, touched },
    isRequired
  } = useInput(props);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  return (
    <FormControl
      fullWidth
      variant="filled"
      margin="dense"
      className={classnames(
        classes.root,
        className,
        'MuiFilledInput-root',
        'MuiFilledInput-underline'
      )}
    >
      <Grid container alignItems="center">
        <FormDataConsumer>
          {({ formData }) => {
            if (formData?.upload) {
              delete formData.upload;
            }

            const value = get(formData, source);

            if (isUploading || !value) {
              return null;
            }

            return value.type?.match(/(image\/)|(application\/svg\+xml)/i) ? (
              <img alt="" src={value.url} style={{ maxWidth: '180px' }} />
            ) : (
              <UrlField source="url" record={value} />
            );
          }}
        </FormDataConsumer>

        <FileInput
          accept={mimeTypes.join(',')}
          className={classnames(classes.upload)}
          disabled={isUploading}
          error={error}
          helperText={helperText}
          isRequired={isRequired}
          label={label}
          multiple={false}
          source="upload"
          touched={!!touched}
          onChange={file => {
            if (file) {
              setIsUploading(true);
              const upload = new Upload(file, {
                endpoint: config.UPLOAD,
                removeFingerprintOnSuccess: true,
                headers: {
                  Authorization: `Bearer ${localStorage.getItem(
                    'feathers-jwt'
                  )}`
                },
                retryDelays: [0, 3000, 5000, 10000, 20000],
                metadata: {
                  name: file.name,
                  type: file.type
                },
                onError: function (error) {
                  console.error(error);
                  setUploadProgress(0);
                  setIsUploading(false);
                },
                onProgress: function (bytesUploaded, bytesTotal) {
                  const progress = (bytesUploaded / bytesTotal) * 100;
                  setUploadProgress(progress);
                },
                onSuccess: function () {
                  const { name, size, type } = file;
                  const { url } = upload;

                  const path = `/uploads${url?.split('/uploads')[1]}`;
                  const value = { name, size, type, url, path };

                  onChange({ target: { value } });
                  setUploadProgress(0);
                  setIsUploading(false);
                }
              });
              upload.start();
            }
          }}
        >
          <ImageField source="src" title="title" />
        </FileInput>

        <div className={classes.progress}>
          {isUploading && (
            <LinearProgress variant="determinate" value={uploadProgress} />
          )}
        </div>
      </Grid>
    </FormControl>
  );
}
